import { Component } from "react";

import Select from "react-select";
import GoalMetricType from "./GoalMetricType";
import GoalConversionType from "./GoalConversionType";
import GoalSections from "./GoalSections";
import RadioButtons from "../common/RadioButtons";
import moment from "moment-timezone";

type Props = {
	id: number;
	isEdit: boolean;
	data: any;
	timezone?: string;
	updateData: Function;
};

const recurrences = ["onetime", "daily", "weekly", "monthly"];
const chartTypes = ["donut", "line"];
const weekdaysOptions = [
	{ label: "Monday", value: "mon" },
	{ label: "Tuesday", value: "tue" },
	{ label: "Wednesday", value: "wed" },
	{ label: "Thursday", value: "thu" },
	{ label: "Friday", value: "fri" },
	{ label: "Saturday", value: "sat" },
	{ label: "Sunday", value: "sun" },
];

const getHours = function (from: number, to: number, include: number[] = []) {
	const hoursSet = new Set<number>();
	
	for (let i = from; i <= to; i++) {
		hoursSet.add(i);
	}

	// Add explicitly included hours
	include.forEach((h) => hoursSet.add(h));

	// Sort the final hours array
	const sortedHours = Array.from(hoursSet).sort((a, b) => a - b);

	return sortedHours.map((h) => {
		const value = ("0" + h).slice(-2) + ":00";
		return {
			value,
			label: value,
		};
	});
};

const getDaysOptions = function (from: number, to: number) {
	const daysArray = [];
	for (let i = from; i <= to; i++) {
		daysArray.push(i);
	}

	return daysArray.map((d) => ({
		value: d,
		label: d,
	}));
};

const getWeeklyEndDayOptions = function (startDay: string) {
	const startDayIndex = weekdaysOptions.findIndex((option) => option.value === startDay);

	return weekdaysOptions.slice(startDayIndex, weekdaysOptions.length);
};

class ConfigItem extends Component<Props> {
	brandId: string;

	constructor(props: Props) {
		super(props);

		const hostname = window.location.hostname;
		const splitIndex = hostname.search(/(\.|-)/);
		this.brandId = hostname.substring(0, splitIndex);
	}

	componentDidUpdate(prevProps: any) {
		if (prevProps.data.metricType !== this.props.data.metricType) {
			if (this.props.data.metricType === 'totalReadingTime') {
				this.props.updateData("recurrencyType", "daily");
			}
		}
	}

	getDefaultTypeConfig = (type: string) => {
		return {
			content_type: "pageviews",
			compared_to: "yesterday",
			cell_content_type: "metric",
		};
	};

	handleTargetChange = (e: any) => {
		const target = +e.target.value.replace(/(\D)/g, "");
		this.props.updateData("target", Math.min(target, 999_999_999));
	};

	getStartHours = () => {
		if (this.props.data.recurrencyType === "onetime") {
			const startDateObj = moment(this.props.data.startDate);

			const timezone = this.props.timezone ?? moment.tz.guess();
			const currentDayObj = moment(moment().tz(timezone).format("YYYY-MM-DD"));
			const currentHourByTimezone = moment().tz(timezone).get('hour');

			if (startDateObj.isSame(currentDayObj)) {
				return getHours(currentHourByTimezone, 23);
			} else {
				return getHours(0, 23);
			}
		}

		if (this.props.data.recurrencyType === 'daily') {
			return getHours(0, 22);
		}

		return getHours(0, 23);
	};

	getEndHours = () => {
		const startTimeAsNumber = parseInt(this.props.data.startTime.substring(0, 2));
		if (isNaN(startTimeAsNumber)) {
			return getHours(0, 23);
		}

		if (this.props.data.recurrencyType === "onetime") {
			const startDateObj = moment(this.props.data.startDate);
			const endDateObj = moment(this.props.data.endDate);

			if (!startDateObj.isSame(endDateObj)) {
				return getHours(0, 23);
			}
		}

		if (this.props.data.recurrencyType === "weekly") {
			return getHours(0, 23);
		}

		if (this.props.data.recurrencyType === "monthly") {
			if (this.props.data.monthlyStartDay !== this.props.data.monthlyEndDay) {
				return getHours(0, 23);
			}
		}

		if (this.props.data.recurrencyType === "daily") {
			return getHours(startTimeAsNumber + 1, 23, [0]);
		}
		
		return getHours(startTimeAsNumber + 1, 23);
	}

	updateTimeForSameDay = () => {
		// settimeout so it executes after rerender and startTime, startDay and recurrencyType values are updated
		setTimeout(() => {
			const startTimeAsNumber = parseInt(this.props.data.startTime.substring(0, 2));
			const endTimeAsNumber = parseInt(this.props.data.endTime.substring(0, 2));
			
			if (!isNaN(startTimeAsNumber) && !isNaN(endTimeAsNumber)) {
				if (startTimeAsNumber === 23) {
					switch (this.props.data.recurrencyType) {
						case 'onetime':
							this.props.updateData("endDate", moment(this.props.data.startDate).add(1, 'day').format("YYYY-MM-DD"));
							this.props.updateData("endTime", '00:00');
							break;
						case 'daily':
							this.props.updateData("startTime", '22:00');
							this.props.updateData("endTime", '23:00');
							break;
						case 'weekly':
							const startDayIndex = weekdaysOptions.findIndex((option) => option.value === (this.props.data.weeklyStartDay));
	
							if (startDayIndex === 6) {
								this.props.updateData("startTime", '22:00');
								this.props.updateData("endTime", '23:00');
							} else {
								const newWeeklyEndDay = weekdaysOptions[startDayIndex + 1].value;
								this.props.updateData("weeklyEndDay", newWeeklyEndDay);
								this.props.updateData("endTime", '00:00');
							}
							break;
						case 'monthly':
							const monthlyStartDay = this.props.data.monthlyStartDay;
	
							if (monthlyStartDay === 31) {
								this.props.updateData("startTime", '22:00');
								this.props.updateData("endTime", '23:00');
							} else {
								const newMonthlyEndDay = monthlyStartDay + 1;
								this.props.updateData("monthlyEndDay", newMonthlyEndDay);
								this.props.updateData("endTime", '00:00');
							}
							break;
						default:
							break;
					}
				} else if (startTimeAsNumber === 0 && endTimeAsNumber === 0) {
					this.props.updateData("endTime", '23:00');
				} else if (endTimeAsNumber <= startTimeAsNumber) {
					this.props.updateData("endTime", '23:00');
				}
			}
		}, 1);
	};

	handleStartDateChange = (e: any) => {
		const date = e.target.value;
		this.props.updateData("startDate", date);

		const startDate = moment(date);
		const endDate = moment(this.props.data.endDate);

		if (startDate.isAfter(endDate)) {
			const startTimeAsNumber = parseInt(this.props.data.startTime.substring(0, 2));
			const endTimeAsNumber = parseInt(this.props.data.endTime.substring(0, 2));

			if (startTimeAsNumber === 23 || (endTimeAsNumber <= startTimeAsNumber)) {
				this.props.updateData("endDate", startDate.clone().add(1, "day").format("YYYY-MM-DD"));
				this.props.updateData('endTime', "00:00");
			} else {
				this.props.updateData("endDate", startDate.format("YYYY-MM-DD"));
			}
		} else if (startDate.isSame(endDate)) {
			this.updateTimeForSameDay();
		}
	};

	handleStartTimeChange = (e: any) => {
		this.props.updateData("startTime", e.value);
		
		const startTimeAsNumber = parseInt(e.value.substring(0, 2));
		const endTimeAsNumber = parseInt(this.props.data.endTime.substring(0, 2));
		
		if (!isNaN(startTimeAsNumber) && !isNaN(endTimeAsNumber)) {
			if (this.props.data.recurrencyType === "onetime") {
				const startDate = moment(this.props.data.startDate);
				const endDate = moment(this.props.data.endDate);
				 
				if (startDate.isSame(endDate)) {
					this.updateTimeForSameDay();
				}
			} else if (this.props.data.recurrencyType === "daily") {
				if (endTimeAsNumber <= startTimeAsNumber && !(endTimeAsNumber === 0 && startTimeAsNumber === 0)) {
					this.props.updateData("endTime", '23:00');
				}
			} else if (this.props.data.recurrencyType === "monthly") {
				const startDay = this.props.data.monthlyStartDay;
				const endDay = this.props.data.monthlyEndDay;

				if (startDay === endDay) {
					this.updateTimeForSameDay();
				}
			}
		}
	};

	handleEndDateChange = (e: any) => {
		const date = e.target.value;
		this.props.updateData("endDate", date);

		const startDate = moment(this.props.data.startDate);
		const endDate = moment(date);

		if (startDate.isSame(endDate)) {
			this.updateTimeForSameDay();
		}
	};

	handleEndTimeChange = (e: any) => {
		this.props.updateData("endTime", e.value);
	};

	handleRecurrence = (recurrence: any) => {
		this.props.updateData("recurrencyType", recurrence);
		
		if (recurrence === "daily") {
			const startTimeAsNumber = parseInt(this.props.data.startTime.substring(0, 2));
			const endTimeAsNumber = parseInt(this.props.data.endTime.substring(0, 2));

			if (startTimeAsNumber >= 22) {
				this.props.updateData("startTime", '22:00');
				this.props.updateData("endTime", '23:00');
			} else if (endTimeAsNumber <= startTimeAsNumber && !(endTimeAsNumber === 0 && startTimeAsNumber === 0)) {
				this.props.updateData("endTime", '23:00');
			}
		} else if (recurrence === "weekly") {
			const startDayIndex = weekdaysOptions.findIndex((option) => option.value === this.props.data.weeklyStartDay);
			const endDayIndex = weekdaysOptions.findIndex((option) => option.value === this.props.data.weeklyEndDay);

			if (startDayIndex === endDayIndex) {
				this.updateTimeForSameDay();
			}
		} else if (recurrence === "monthly") {
			const startDay = this.props.data.monthlyStartDay;
			const endDay = this.props.data.monthlyEndDay;

			if (startDay === endDay) {
				this.updateTimeForSameDay();
			}
		} else if (recurrence === "onetime") {
			const startDate = moment(this.props.data.startDate);
			const endDate = moment(this.props.data.endDate);

			if (startDate.isSame(endDate)) {
				this.updateTimeForSameDay();
			}
		}
	};

	handleWeeklyRecurDay = (recurDay: any) => {
		this.props.updateData("weeklyRecurDay", recurDay);
	};

	handleWeeklyStartDayChange = (e: any) => {
		const day = e.value;
		this.props.updateData("weeklyStartDay", day);
	};

	handleWeeklyEndDayChange = (e: any) => {
		const day = e.value;
		this.props.updateData("weeklyEndDay", day);
	}

	handleMonthlyType = (monthlyType: any) => {
		this.props.updateData("monthlyType", monthlyType);
	};

	handleMonthlyRecurDay = (e: any) => {
		this.props.updateData("monthlyRecurDay", e.value);
	};

	handleMonthlyRecurDate = (e: any) => {
		this.props.updateData("monthlyRecurDate", e.value);
	};

	handleMonthlyStartDayChange = (e: any) => {
		const startDay = e.value;
		this.props.updateData("monthlyStartDay", startDay);
		
		let endDay = this.props.data.monthlyEndDay;
		
		if (startDay === endDay) {
			this.updateTimeForSameDay();
		}
	};

	handleMonthlyEndDayChange = (e: any) => {
		const endDay = e.value;
		this.props.updateData("monthlyEndDay", endDay);

		const startDay = this.props.data.monthlyStartDay;

		if (startDay === endDay) {
			this.updateTimeForSameDay();
		}
	};

	setNeverending = (option: string) => {
		if (option === "never") {
			this.props.updateData("neverending", true);
		} else {
			const now = moment().utc();
			this.props.updateData("neverending", false);
			if (moment(this.props.data.endDate).diff(now, "y") > 50)
				this.props.updateData("endDate", now.clone().add(1, "month").endOf("month").format("YYYY-MM-DD"));
		}
	};

	setWholeMonth = (option: string) => {
		if (option === "custom") this.props.updateData("monthlyWhole", false);
		else this.props.updateData("monthlyWhole", true);
	};

	setChartType = (option: string) => {
		this.props.updateData("chartType", option);
	};

	renderDetailedGoalConfig = (recurrencyType: string) => {
		const { data } = this.props;
		const nowTz = moment().tz(this.props.timezone ?? moment.tz.guess()).format("YYYY-MM-DD");

		const toggleDay = (day: string) => {
			const dailyRecurDays = { ...this.props.data.dailyRecurDays };
			dailyRecurDays[day] = !dailyRecurDays[day];
			this.props.updateData("dailyRecurDays", dailyRecurDays);
		};
		if (recurrencyType === "onetime")
			return (
				<>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Start Date</label>
							<div className="datetime">
								<input
									className="form-control"
									type="date"
									onChange={this.handleStartDateChange}
									min={nowTz}
									value={data.startDate as string}
								/>
								<Select
									isDisabled={!data.startDate}
									id="start-date"
									className="time-picker"
									classNamePrefix="so-select"
									options={this.getStartHours()}
									value={{
										label: data.startTime,
										value: data.startTime,
									}}
									onChange={this.handleStartTimeChange}
									isSearchable={false}
								/>
							</div>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="end-date">End Date</label>
							<div className="datetime">
								<input
									className="form-control"
									type="date"
									min={data.startDate}
									onChange={this.handleEndDateChange}
									value={data.endDate as string}
								/>
								<Select
									isDisabled={!data.endDate}
									id="end-date"
									className="time-picker"
									classNamePrefix="so-select"
									options={this.getEndHours()}
									value={{
										label: data.endTime,
										value: data.endTime,
									}}
									onChange={this.handleEndTimeChange}
									isSearchable={false}
								/>
							</div>
						</div>
					</div>
				</>
			);

		if (recurrencyType === "daily")
			return (
				<>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Repeat every:</label>
							<div className="checkboxes">
								<label className="control control-checkbox" htmlFor="checkbox-daily-mon">
									<input
										type="checkbox"
										id="checkbox-daily-mon"
										onChange={() => toggleDay("mon")}
										checked={data.dailyRecurDays.mon}
									/>
									<div className="control-indicator"></div>
									Mon
								</label>
								<label className="control control-checkbox" htmlFor="checkbox-daily-tue">
									<input
										type="checkbox"
										id="checkbox-daily-tue"
										onChange={() => toggleDay("tue")}
										checked={data.dailyRecurDays.tue}
									/>
									<div className="control-indicator"></div>
									Tue
								</label>
								<label className="control control-checkbox" htmlFor="checkbox-daily-wed">
									<input
										type="checkbox"
										id="checkbox-daily-wed"
										onChange={() => toggleDay("wed")}
										checked={data.dailyRecurDays.wed}
									/>
									<div className="control-indicator"></div>
									Wed
								</label>
								<label className="control control-checkbox" htmlFor="checkbox-daily-thu">
									<input
										type="checkbox"
										id="checkbox-daily-thu"
										onChange={() => toggleDay("thu")}
										checked={data.dailyRecurDays.thu}
									/>
									<div className="control-indicator"></div>
									Thu
								</label>
								<label className="control control-checkbox" htmlFor="checkbox-daily-fri">
									<input
										type="checkbox"
										id="checkbox-daily-fri"
										onChange={() => toggleDay("fri")}
										checked={data.dailyRecurDays.fri}
									/>
									<div className="control-indicator"></div>
									Fri
								</label>
								<label className="control control-checkbox" htmlFor="checkbox-daily-sat">
									<input
										type="checkbox"
										id="checkbox-daily-sat"
										onChange={() => toggleDay("sat")}
										checked={data.dailyRecurDays.sat}
									/>
									<div className="control-indicator"></div>
									Sat
								</label>
								<label className="control control-checkbox" htmlFor="checkbox-daily-sun">
									<input
										type="checkbox"
										id="checkbox-daily-sun"
										onChange={() => toggleDay("sun")}
										checked={data.dailyRecurDays.sun}
									/>
									<div className="control-indicator"></div>
									Sun
								</label>
							</div>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Data period:</label>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Start Time</label>
							<Select
								id="start-date"
								className="basic-single"
								classNamePrefix="so-select"
								options={this.getStartHours()}
								value={{
									label: data.startTime,
									value: data.startTime,
								}}
								onChange={this.handleStartTimeChange}
								isSearchable={false}
							/>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="end-date">End Time</label>
							<Select
								id="end-date"
								className="basic-single"
								classNamePrefix="so-select"
								options={this.getEndHours()}
								value={{
									label: data.endTime,
									value: data.endTime,
								}}
								onChange={this.handleEndTimeChange}
								isSearchable={false}
							/>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Goal Ends</label>
							<RadioButtons
								options={["never", "on-date"]}
								onChange={this.setNeverending}
								value={data.neverending ? "never" : "on-date"}
							/>
						</div>
					</div>
					{!data.neverending && (
						<div className="config-item__selects">
							<div className="form-group">
								<label htmlFor="end-date">Ends on</label>
								<div className="date-only">
									<input
										className="form-control"
										type="date"
										min={nowTz}
										onChange={this.handleEndDateChange}
										value={data.endDate as string}
									/>
								</div>
							</div>
						</div>
					)}
				</>
			);

		if (recurrencyType === "weekly")
			return (
				<>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Data period:</label>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Start Day</label>
							<div className="datetime">
								<Select
									isDisabled={!data.startDate}
									id="start-date"
									className="day-picker"
									classNamePrefix="so-select"
									options={weekdaysOptions}
									value={weekdaysOptions.find((i) => i.value === this.props.data.weeklyStartDay)}
									onChange={this.handleWeeklyStartDayChange}
									isSearchable={false}
								/>
								<Select
									isDisabled={!data.startDate}
									id="start-date"
									className="time-picker"
									classNamePrefix="so-select"
									options={this.getStartHours()}
									value={{
										label: data.startTime,
										value: data.startTime,
									}}
									onChange={this.handleStartTimeChange}
									isSearchable={false}
								/>
							</div>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">End Day</label>
							<div className="datetime">
								<Select
									isDisabled={!data.startDate}
									id="start-date"
									className="day-picker"
									classNamePrefix="so-select"
									options={weekdaysOptions}
									value={weekdaysOptions.find((i) => i.value === this.props.data.weeklyEndDay)}
									onChange={this.handleWeeklyEndDayChange}
									isSearchable={false}
								/>
								<Select
									isDisabled={!data.startDate}
									id="start-date"
									className="time-picker"
									classNamePrefix="so-select"
									options={this.getEndHours()}
									value={{
										label: data.endTime,
										value: data.endTime,
									}}
									onChange={this.handleEndTimeChange}
									isSearchable={false}
								/>
							</div>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Goal Ends</label>
							<RadioButtons
								options={["never", "on-date"]}
								onChange={this.setNeverending}
								value={data.neverending ? "never" : "on-date"}
							/>
						</div>
					</div>
					{!data.neverending && (
						<div className="config-item__selects">
							<div className="form-group">
								<label htmlFor="end-date">Ends on</label>
								<div className="date-only">
									<input
										className="form-control"
										type="date"
										min={nowTz}
										onChange={this.handleEndDateChange}
										value={data.endDate as string}
									/>
								</div>
							</div>
						</div>
					)}
				</>
			);

		if (recurrencyType === "monthly")
			return (
				<>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Data period:</label>
						</div>
					</div>
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Run For</label>
							<RadioButtons
								options={["whole-month", "custom"]}
								onChange={this.setWholeMonth}
								value={data.monthlyWhole ? "whole-month" : "custom"}
							/>
						</div>
					</div>
					{!data.monthlyWhole && (
						<>
							<div className="config-item__selects">
								<div className="form-group">
									<label htmlFor="start-date">Start Day</label>
									<div className="datetime">
										<Select
											isDisabled={!data.startDate}
											id="start-date"
											className="day-picker"
											classNamePrefix="so-select"
											options={getDaysOptions(1, 31)}
											value={{label: this.props.data.monthlyStartDay, value: this.props.data.monthlyStartDay}}
											onChange={this.handleMonthlyStartDayChange}
											isSearchable={false}
										/>
										<Select
											isDisabled={!data.startDate}
											id="start-date"
											className="time-picker"
											classNamePrefix="so-select"
											options={this.getStartHours()}
											value={{
												label: data.startTime,
												value: data.startTime,
											}}
											onChange={this.handleStartTimeChange}
											isSearchable={false}
										/>
									</div>
								</div>
							</div>
							<div className="config-item__selects">
								<div className="form-group">
									<label htmlFor="start-date">End Day</label>
									<div className="datetime">
										<Select
											isDisabled={!data.startDate}
											id="start-date"
											className="day-picker"
											classNamePrefix="so-select"
											options={getDaysOptions(1, 31)}
											value={{label: this.props.data.monthlyEndDay, value: this.props.data.monthlyEndDay}}
											onChange={this.handleMonthlyEndDayChange}
											isSearchable={false}
										/>
										<Select
											isDisabled={!data.startDate}
											id="start-date"
											className="time-picker"
											classNamePrefix="so-select"
											options={this.getEndHours()}
											value={{
												label: data.endTime,
												value: data.endTime,
											}}
											onChange={this.handleEndTimeChange}
											isSearchable={false}
										/>
									</div>
								</div>
							</div>
						</>
					)}
					<div className="config-item__selects">
						<div className="form-group">
							<label htmlFor="start-date">Goal Ends</label>
							<RadioButtons
								options={["never", "on-date"]}
								onChange={this.setNeverending}
								value={data.neverending ? "never" : "on-date"}
							/>
						</div>
					</div>
					{!data.neverending && (
						<div className="config-item__selects">
							<div className="form-group">
								<label htmlFor="end-date">Ends on</label>
								<div className="date-only">
									<input
										className="form-control"
										type="date"
										min={nowTz}
										onChange={this.handleEndDateChange}
										value={data.endDate as string}
									/>
								</div>
							</div>
						</div>
					)}
				</>
			);
	};

	render() {
		const { data } = this.props;
		return (
			<div className="config-item pb-5">
				<div className="config-item__body">
					<div className="config-item__list">
						<GoalMetricType
							updateConfig={this.props.updateData}
							value={data.metricType}
							isDisabled={this.props.isEdit}
						/>
						{data.metricType === "conversion" && (
							<GoalConversionType
								updateConfig={this.props.updateData}
								brandId={this.brandId}
								value={data.conversions}
								isEdit={this.props.isEdit}
							/>
						)}
						<div className="config-item__selects">
							<div className="form-group">
								<label htmlFor="target">Target</label>
								<input
									className="form-control"
									value={data.target}
									onChange={this.handleTargetChange}
									placeholder="E.g. 10000"
									max={Number.MAX_SAFE_INTEGER}
									min={0}
								/>
							</div>
						</div>
						{data.metricType !== "conversion" && (
							<GoalSections
								updateConfig={this.props.updateData}
								sections={data.sections}
								isEdit={this.props.isEdit}
								type={data.metricType === "videoPageViews" ? "videos" : "feed"}
								brandId={this.brandId}
							/>
						)}
						<div className="config-item__selects">
							<div className="form-group">
								<label htmlFor="recurrence">Repeat</label>
								<RadioButtons
									value={data.recurrencyType}
									onChange={this.handleRecurrence}
									options={recurrences}
									isDisabled={this.props.isEdit || this.props.data.metricType === "totalReadingTime"}
								/>
							</div>
						</div>
						{this.renderDetailedGoalConfig(data.recurrencyType)}
						<div className="config-item__show-circles mb-2 mt-2">
							<label className="control control-checkbox" htmlFor="checkbox-show-circles">
								<input
									type="checkbox"
									id="checkbox-show-circles"
									onChange={() => this.props.updateData("displayTimeleft", !data.displayTimeleft)}
									value="show_circles"
									checked={data.displayTimeleft}
								/>
								<div className="control-indicator"></div>
								Display time left
							</label>
						</div>
						<div className="config-item__selects">
							<div className="form-group">
								<label htmlFor="start-date">Chart type</label>
								<RadioButtons
									options={chartTypes}
									onChange={this.setChartType}
									value={data.chartType}
									isDisabled={["totalVisitors", "appVisitors", "webVisitors"].includes(data.metricType)}
								/>
							</div>
						</div>
						{data.recurrencyType !== "daily" && data.chartType === "line" && (
							<div className="config-item__show-circles mb-2 mt-2">
								<label className="control control-checkbox" htmlFor="checkbox-autorotate">
									<input
										type="checkbox"
										id="checkbox-autorotate"
										onChange={() =>
											this.props.updateData("lineChartAutoRotate", !data.lineChartAutoRotate)
										}
										value="show_circles"
										checked={data.lineChartAutoRotate}
									/>
									<div className="control-indicator"></div>
									Auto rotate line chart carousel
								</label>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

export default ConfigItem;
